/**
 * ConceptCanvas - Concept editor wrapper around BaseCanvas
 * Adds concept-specific features:
 * - Local state management (not PlayContext)
 * - Default 5 linemen initialization
 * - Assignment extraction for saving concepts
 * - Targeting mode support
 * - No history/undo (explicit save)
 */

import React, { useEffect, useState } from 'react'
import { BaseCanvas } from './BaseCanvas'
import { CanvasDialogs } from './CanvasDialogs'
import { useCanvasCallbacks, useStateRef } from './hooks'
import { createDefaultLinemen } from '@/utils/Lineman.utils'
import { flipCanvasHorizontally } from '@/utils/Flip.utils'
import type { Player } from '@/types'
import type { Drawing, DrawingState, HashAlignment } from '@/types'
import type { TargetingMode } from '@/types/Concept.types'

interface ConceptCanvasProps {
	drawingState: DrawingState
	hashAlignment: HashAlignment
	targetingMode?: TargetingMode
	initialAssignments?: ConceptAssignment[]
	onCanvasStateReady?: (getState: () => {
		drawings: Drawing[],
		players: Player[],
		targetingMode: TargetingMode
	}) => void
	onFlipReady?: (flipFn: () => void) => void
	readonly?: boolean
}

// UI layer assignment type (subset for creating new assignments)
// Matches database schema from Concept.types
export interface ConceptAssignment {
	role: string | null
	selector_type: string | null
	selector_params: Record<string, unknown> | null
	drawing_data: any
}

/**
 * ConceptCanvas Component
 * Wraps BaseCanvas with local state for concept editing
 */
export function ConceptCanvas({
								  drawingState,
								  hashAlignment,
								  targetingMode = 'absolute_role',
								  initialAssignments,
								  onCanvasStateReady,
								  onFlipReady,
								  readonly = false,
							  }: ConceptCanvasProps) {
	// Local state (not PlayContext)
	const [drawings, setDrawings] = useState<Drawing[]>([])
	const [players, setPlayers] = useState<Player[]>(() =>
		createDefaultLinemen(hashAlignment),
	)
	const [isInitialized, setIsInitialized] = useState(false)

	// Load initial assignments if provided (edit mode)
	useEffect(() => {
		if (!isInitialized && initialAssignments && initialAssignments.length > 0) {
			const reconstructed = reconstructFromAssignments(initialAssignments, hashAlignment)
			setPlayers(reconstructed.players)
			setDrawings(reconstructed.drawings)
			setIsInitialized(true)
		}
	}, [initialAssignments, hashAlignment, isInitialized])

	// Provide getState function to parent for on-demand state access
	// Use ref to keep state accessible without triggering parent re-renders
	useStateRef({ drawings, players, targetingMode }, onCanvasStateReady)

	// Provide flip function to parent
	useEffect(() => {
		if (onFlipReady) {
			const flipCanvas = () => {
				const flipped = flipCanvasHorizontally(players, drawings)
				setPlayers(flipped.players)
				setDrawings(flipped.drawings)
			}
			onFlipReady(flipCanvas)
		}
	}, [players, drawings, onFlipReady])

	// Build callbacks for local state management
	const callbacks = useCanvasCallbacks({
		setDrawings,
		setPlayers,
	})

	// Render dialogs via render prop
	function renderDialogs(state: any, actions: any) {
		return (
			<CanvasDialogs
				state={state}
				actions={actions}
				players={players}
				drawings={drawings}
				callbacks={callbacks}
			/>
		)
	}

	return (
		<BaseCanvas
			drawingState={drawingState}
			drawings={drawings}
			players={players}
			callbacks={callbacks}
			hashAlignment={hashAlignment}
			containerMode="fill" // Concept canvas fills container
			showFieldMarkings={false}
			showPlayBar={false} // No play bar in concept editor
			readonly={readonly}
			renderDialogs={renderDialogs}
			// No onBeforeStateChange - concepts have explicit save, no history
		/>
	)
}

/**
 * Extract player assignments from canvas state for saving
 * Called on-demand when saving, not automatically on every change
 */
export function extractAssignments(
	drawings: Drawing[],
	players: Player[],
	targetingMode: TargetingMode,
): ConceptAssignment[] {
	return drawings
		.filter((d) => d.playerId) // Only linked drawings
		.map((drawing) => {
			const player = players.find((p) => p.id === drawing.playerId)
			return {
				role: player?.label || null,
				selector_type: null, // Will be set when using Alignment Based targeting
				selector_params: null, // Will be set when using Alignment Based targeting
				drawing_data: serializeDrawing(drawing),
			}
		})
		.filter((a) => a.role) // Filter out assignments with empty roles
}

/**
 * Serialize drawing for storage
 * Converts drawing object to JSON-compatible format
 */
function serializeDrawing(drawing: Drawing): any {
	return {
		id: drawing.id,
		points: drawing.points,
		segments: drawing.segments,
		style: drawing.style,
		preSnapMotion: drawing.preSnapMotion,
		linkedPointId: drawing.linkedPointId,
	}
}

/**
 * Reconstruct canvas state from saved assignments
 * Reverses the extractAssignments process
 */
export function reconstructFromAssignments(
	assignments: ConceptAssignment[],
	hashAlignment: HashAlignment,
): { players: Player[]; drawings: Drawing[] } {
	if (!assignments || assignments.length === 0) {
		return {
			players: createDefaultLinemen(hashAlignment),
			drawings: [],
		}
	}

	const players: Player[] = []
	const drawings: Drawing[] = []
	const playersByRole = new Map<string, Player>()

	// Process each assignment
	assignments.forEach((assignment) => {
		const role = assignment.role
		const drawingData = assignment.drawing_data

		if (!role || !drawingData) return

		// Get or create player for this role
		let player = playersByRole.get(role)
		if (!player) {
			// Create player at a default position (will be positioned by linked drawing)
			player = {
				id: crypto.randomUUID(),
				x: 0,
				y: 0,
				label: role,
				color: '#3b82f6', // Default skill player color
			}
			players.push(player)
			playersByRole.set(role, player)
		}

		// Reconstruct drawing
		const drawing: Drawing = {
			id: drawingData.id || crypto.randomUUID(),
			points: drawingData.points || [],
			segments: drawingData.segments || [],
			style: drawingData.style || {
				color: '#000000',
				strokeWidth: 3,
				lineStyle: 'solid',
				lineEnd: 'arrow',
				pathMode: 'sharp',
			},
			playerId: player.id, // Link to player
			linkedPointId: drawingData.linkedPointId,
			preSnapMotion: drawingData.preSnapMotion,
			annotations: [], // Initialize empty annotations array
		}

		// Position player at the start of their route (first point)
		if (Array.isArray(drawing.points) && drawing.points.length > 0 && drawing.linkedPointId) {
			const linkedPoint = drawing.points.find((p) => p.id === drawing.linkedPointId)
			if (linkedPoint) {
				player.x = linkedPoint.x
				player.y = linkedPoint.y
			}
		}

		drawings.push(drawing)
	})

	// If no players were created, use default linemen
	if (players.length === 0) {
		return {
			players: createDefaultLinemen(hashAlignment),
			drawings: [],
		}
	}

	return { players, drawings }
}
